// src/services/tokenService.js
import { ethers } from "ethers";
import { TOKEN_NAMES } from "../constants/tokenData";

export const fetchTokens = async (contract) => {
  if (!contract) return [];
  
  try {
    // Try to find out how many tokens are in the fund
    const tokenList = [];
    let index = 0;
    let continueLoop = true;
    
    while (continueLoop && index < 10) { // Limit to 10 to prevent infinite loops
      try {
        const tokenInfo = await contract.getTokenInfo(index);
        tokenList.push({
          index,
          address: tokenInfo.tokenAddress,
          allocation: ethers.utils.formatUnits(tokenInfo.allocation, 0),
          priceFeed: tokenInfo.priceFeed
        });
        index++;
      } catch (error) {
        // If we get an error, we've reached the end of the array
        continueLoop = false;
      }
    }
    
    console.log(`Found ${tokenList.length} tokens in the fund`);
    return tokenList;
  } catch (error) {
    console.error("Error fetching token list:", error);
    return [];
  }
};

export const fetchTokenDetails = async (tokenAddress, provider) => {
  if (!tokenAddress || !provider) return { name: "Unknown", balance: "0", decimals: 18 };
  
  try {
    // Get token name (either from our map or try to fetch from contract)
    let tokenName = TOKEN_NAMES[tokenAddress] || "Unknown";
    
    // Try to get token balance using ERC20 interface
    let decimals = 18;
    try {
      const tokenContract = new ethers.Contract(
        tokenAddress,
        [
          "function name() view returns (string)",
          "function decimals() view returns (uint8)"
        ],
        provider
      );
      
      // Try to get token name from contract if not in our map
      if (tokenName === "Unknown") {
        try {
          tokenName = await tokenContract.name();
        } catch (e) {
          console.log("Could not get name for token:", tokenAddress);
        }
      }
      
      // Try to get token decimals
      try {
        decimals = await tokenContract.decimals();
      } catch (e) {
        console.log("Could not get decimals for token:", tokenAddress);
      }
    } catch (e) {
      console.log("Error getting token details:", e);
    }
    
    return { name: tokenName, decimals };
  } catch (error) {
    console.error("Error fetching token details:", error);
    return { name: "Unknown", decimals: 18 };
  }
};

export const fetchTokenBalance = async (tokenAddress, contractAddress, provider) => {
  if (!tokenAddress || !contractAddress || !provider) return "0";
  
  try {
    const tokenContract = new ethers.Contract(
      tokenAddress,
      ["function balanceOf(address) view returns (uint256)"],
      provider
    );
    
    const balance = await tokenContract.balanceOf(contractAddress);
    return balance;
  } catch (error) {
    console.error("Error fetching token balance:", error);
    return ethers.BigNumber.from(0);
  }
};

export const enhanceTokens = async (tokens, provider, contractAddress) => {
  if (!tokens || tokens.length === 0 || !provider || !contractAddress) return [];
  
  const enhancedTokens = await Promise.all(tokens.map(async (token) => {
    try {
      // Get token details
      const { name, decimals } = await fetchTokenDetails(token.address, provider);
      
      // Get token balance
      const rawBalance = await fetchTokenBalance(token.address, contractAddress, provider);
      const balance = ethers.utils.formatUnits(rawBalance, decimals);
      
      return {
        ...token,
        name,
        balance,
        decimals
      };
    } catch (error) {
      console.error("Error enhancing token:", error);
      return {
        ...token,
        name: "Unknown",
        balance: "0",
        decimals: 18
      };
    }
  }));
  
  return enhancedTokens;
};